<template>
  <Dialog :open="open" @update:open="handleClose" :title="dialogTitle" size="xl">
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- Model Information Section -->
      <div class="space-y-4">
        <div>
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Model Information</h4>
          <div class="space-y-4">
            <!-- Name Input -->
            <div>
              <label
                for="asab-model-name"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Name <span class="text-red-500">*</span>
              </label>
              <Input
                id="asab-model-name"
                v-model="formState.name"
                placeholder="e.g., My ASAB Model"
                :disabled="isSubmitting"
                :error="!!errors.name"
                class="w-full"
              />
              <p v-if="errors.name" class="mt-1.5 text-xs text-red-600 dark:text-red-400">
                {{ errors.name }}
              </p>
              <p v-else class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                A friendly name for your ASAB model
              </p>
            </div>

            <!-- Slug Input -->
            <div>
              <label
                for="asab-model-slug"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Slug <span class="text-red-500">*</span>
              </label>
              <Input
                id="asab-model-slug"
                v-model="formState.slug"
                placeholder="e.g., my-asab-model"
                :disabled="isSubmitting || isEditMode"
                :error="!!errors.slug"
                class="w-full"
              />
              <p v-if="errors.slug" class="mt-1.5 text-xs text-red-600 dark:text-red-400">
                {{ errors.slug }}
              </p>
              <p v-else class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                {{
                  isEditMode
                    ? 'Slug cannot be changed after creation'
                    : 'Alphanumeric characters, dots, underscores, and hyphens only'
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Load Balancing Strategy Display -->
        <div
          class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3"
        >
          <div class="flex items-center space-x-2">
            <svg
              class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <div class="flex-1">
              <p class="text-sm font-medium text-blue-900 dark:text-blue-300">
                Load Balancing Strategy: Weighted Round-Robin
              </p>
              <p class="mt-0.5 text-xs text-blue-700 dark:text-blue-400">
                Traffic is distributed across routing targets based on their weights
              </p>
            </div>
          </div>
        </div>

        <!-- Routing Targets Section -->
        <div>
          <RoutingTargetInput
            v-model="formState.routingTargets"
            :available-base-models="enabledBaseModels"
            :available-custom-models="enabledCustomModels"
          />
          <p v-if="errors.routingTargets" class="mt-1.5 text-xs text-red-600 dark:text-red-400">
            {{ errors.routingTargets }}
          </p>
        </div>

        <!-- Fallback Chain Section -->
        <div>
          <FallbackChainInput
            v-model="formState.fallbackChain"
            :available-base-models="enabledBaseModels"
            :available-custom-models="enabledCustomModels"
          />
        </div>

        <!-- Blended Cost Display -->
        <div
          v-if="formState.routingTargets.length > 0"
          class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
        >
          <div class="flex items-start space-x-3">
            <svg
              class="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div class="flex-1">
              <h4 class="text-sm font-semibold text-green-900 dark:text-green-300 mb-2">
                Real-Time Blended Cost
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Card padding="sm">
                  <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Input Cost</p>
                  <p class="text-lg font-bold text-green-700 dark:text-green-400">
                    ${{ blendedCost.input_1m.toFixed(4) }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">per 1M tokens</p>
                </Card>
                <Card padding="sm">
                  <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Output Cost</p>
                  <p class="text-lg font-bold text-green-700 dark:text-green-400">
                    ${{ blendedCost.output_1m.toFixed(4) }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">per 1M tokens</p>
                </Card>
              </div>
              <p class="mt-2 text-xs text-green-700 dark:text-green-400">
                Calculated as weighted average: Σ(weight × cost) / Σ(weight)
              </p>
            </div>
          </div>
        </div>

        <!-- Rate Limits Section -->
        <div>
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Rate Limits (Optional)
          </h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Set rate limits to control usage. Leave empty for no limits.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- RPM Limit -->
            <div>
              <label
                for="asab-model-rpm"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Requests Per Minute (RPM)
              </label>
              <Input
                id="asab-model-rpm"
                v-model.number="formState.limits.rpm"
                type="number"
                min="1"
                placeholder="e.g., 100"
                :disabled="isSubmitting"
                class="w-full"
              />
              <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Maximum requests per minute
              </p>
            </div>

            <!-- TPM Limit -->
            <div>
              <label
                for="asab-model-tpm"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Tokens Per Minute (TPM)
              </label>
              <Input
                id="asab-model-tpm"
                v-model.number="formState.limits.tpm"
                type="number"
                min="1"
                placeholder="e.g., 10000"
                :disabled="isSubmitting"
                class="w-full"
              />
              <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Maximum tokens per minute
              </p>
            </div>
          </div>
        </div>

        <!-- Enabled Toggle -->
        <Card class="flex items-center justify-between">
          <div>
            <label
              for="asab-model-enabled"
              class="text-sm font-medium text-gray-900 dark:text-white"
            >
              Enable Model
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {{
                formState.isEnabled
                  ? 'Model is enabled and available for use'
                  : 'Model is disabled and not available for use'
              }}
            </p>
          </div>
          <Switch id="asab-model-enabled" v-model="formState.isEnabled" :disabled="isSubmitting" />
        </Card>
      </div>

      <!-- Info Notice -->
      <div
        class="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-purple-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-purple-800 dark:text-purple-300">
              ASAB Model Features
            </h3>
            <div class="mt-1 text-sm text-purple-700 dark:text-purple-400">
              <p>
                ASAB (Augmented Service Access Bridge) models provide advanced routing with load
                balancing, automatic fallback chains, and rate limiting for optimal performance and
                reliability.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Display -->
      <Alert v-if="generalError" variant="error">
        {{ generalError }}
      </Alert>

      <!-- Actions -->
      <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" @click="handleClose" :disabled="isSubmitting">
          Cancel
        </Button>
        <Button type="submit" variant="primary" :loading="isSubmitting">
          {{ isEditMode ? 'Update Model' : 'Create Model' }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Dialog, Button, Input, Alert, Switch, Card } from '@asabdev/asab-ui';
  import { validateName, validateSlug } from '../utils/validation';
  import { useBlendedCost } from '../composables/useBlendedCost';
  import RoutingTargetInput from './RoutingTargetInput.vue';
  import FallbackChainInput from './FallbackChainInput.vue';
  import type {
    AsabModelResponse,
    RoutingTarget,
    FallbackTarget,
    BaseModelResponse,
    CustomModelResponse,
  } from '../types';

  interface Props {
    open: boolean;
    editModel?: AsabModelResponse | null;
    availableBaseModels: BaseModelResponse[];
    availableCustomModels: CustomModelResponse[];
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'created', model: AsabModelResponse): void;
    (e: 'updated', model: AsabModelResponse): void;
    (e: 'cancel'): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    editModel: null,
  });

  const emit = defineEmits<Emits>();

  // Form state
  interface FormState {
    name: string;
    slug: string;
    routingTargets: RoutingTarget[];
    fallbackChain: FallbackTarget[];
    limits: { rpm?: number; tpm?: number };
    isEnabled: boolean;
  }

  const formState = ref<FormState>({
    name: '',
    slug: '',
    routingTargets: [],
    fallbackChain: [],
    limits: {},
    isEnabled: true,
  });

  const isSubmitting = ref(false);
  const errors = ref<Record<string, string>>({});
  const generalError = ref<string | null>(null);

  // Computed properties
  const isEditMode = computed(() => !!props.editModel);

  const dialogTitle = computed(() => {
    return isEditMode.value ? 'Edit ASAB Model' : 'Create ASAB Model';
  });

  // Filter enabled models only
  const enabledBaseModels = computed(() => {
    return props.availableBaseModels.filter(m => m.is_enabled_for_org);
  });

  const enabledCustomModels = computed(() => {
    return props.availableCustomModels.filter(m => m.is_enabled);
  });

  // Use blended cost composable for real-time cost calculation
  const { blendedCost } = useBlendedCost(
    computed(() => formState.value.routingTargets),
    computed(() => props.availableBaseModels),
    computed(() => props.availableCustomModels)
  );

  // Methods
  const validateForm = (): boolean => {
    errors.value = {};
    generalError.value = null;

    // Validate name
    const nameValidation = validateName(formState.value.name);
    if (!nameValidation.isValid) {
      errors.value.name = nameValidation.error || 'Invalid name';
    }

    // Validate slug (only for create mode)
    if (!isEditMode.value) {
      const slugValidation = validateSlug(formState.value.slug);
      if (!slugValidation.isValid) {
        errors.value.slug = slugValidation.error || 'Invalid slug';
      }
    }

    // Validate routing targets
    if (formState.value.routingTargets.length === 0) {
      errors.value.routingTargets = 'At least one routing target is required';
    } else {
      // Validate each routing target has a backend_id and valid weight
      for (const target of formState.value.routingTargets) {
        if (!target.backend_id || target.backend_id.trim() === '') {
          errors.value.routingTargets = 'All routing targets must have a model selected';
          break;
        }
        if (!target.weight || target.weight < 1) {
          errors.value.routingTargets = 'All routing targets must have a weight of at least 1';
          break;
        }
      }
    }

    // Validate rate limits (if provided)
    if (formState.value.limits.rpm !== undefined && formState.value.limits.rpm !== null) {
      if (formState.value.limits.rpm < 1) {
        errors.value.rpm = 'RPM must be at least 1';
      }
    }

    if (formState.value.limits.tpm !== undefined && formState.value.limits.tpm !== null) {
      if (formState.value.limits.tpm < 1) {
        errors.value.tpm = 'TPM must be at least 1';
      }
    }

    return Object.keys(errors.value).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    isSubmitting.value = true;

    // Prepare the model data
    const modelData = {
      name: formState.value.name.trim(),
      slug: formState.value.slug.trim(),
      routing_targets: formState.value.routingTargets,
      fallback_chain: formState.value.fallbackChain.length > 0 ? formState.value.fallbackChain : undefined,
      limits: {
        rpm: formState.value.limits.rpm || null,
        tpm: formState.value.limits.tpm || null,
      },
      is_enabled: formState.value.isEnabled,
    };

    if (isEditMode.value) {
      // For edit mode, create update payload (exclude slug)
      const updateData = {
        name: modelData.name,
        routing_targets: modelData.routing_targets,
        fallback_chain: modelData.fallback_chain,
        limits: modelData.limits,
        is_enabled: modelData.is_enabled,
      };

      emit('updated', updateData as unknown as AsabModelResponse);
    } else {
      emit('created', modelData as unknown as AsabModelResponse);
    }

    // Reset submitting state after a short delay
    setTimeout(() => {
      isSubmitting.value = false;
    }, 500);
  };

  const handleClose = () => {
    if (isSubmitting.value) return;

    resetForm();
    emit('update:open', false);
    emit('cancel');
  };

  const resetForm = () => {
    formState.value = {
      name: '',
      slug: '',
      routingTargets: [],
      fallbackChain: [],
      limits: {},
      isEnabled: true,
    };
    errors.value = {};
    generalError.value = null;
    isSubmitting.value = false;
  };

  const loadEditData = () => {
    if (props.editModel) {
      formState.value = {
        name: props.editModel.name,
        slug: props.editModel.slug,
        routingTargets: [...props.editModel.routing.targets],
        fallbackChain: [...props.editModel.routing.fallback_chain],
        limits: {
          rpm: props.editModel.limits.rpm || undefined,
          tpm: props.editModel.limits.tpm || undefined,
        },
        isEnabled: props.editModel.is_enabled,
      };
    }
  };

  // Watch for modal open/close
  watch(
    () => props.open,
    newValue => {
      if (newValue) {
        if (isEditMode.value) {
          loadEditData();
        } else {
          resetForm();
        }
      } else {
        resetForm();
      }
    },
    { immediate: true }
  );

  // Watch for editModel changes
  watch(
    () => props.editModel,
    () => {
      if (props.open && isEditMode.value) {
        loadEditData();
      }
    },
    { immediate: true }
  );
</script>
